(ns manage.adset-decision-tree-page.editor.edge
  (:require ["@mui/material/Divider$default" :as Divider]
            ["@mui/material/Icon$default" :as Icon]
            ["@mui/material/IconButton$default" :as IconButton]
            ["@mui/material/Paper$default" :as Paper]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/styles" :as styles]
            ["@xyflow/react"
             :refer [BaseEdge EdgeLabelRenderer getBezierPath getStraightPath
                     MarkerType useInternalNode useReactFlow]]
            [cljs-bean.core :refer [->clj ->js]]
            [common.uix.hooks :refer [use-element-presence]]
            [goog.object :as gobject]
            [goog.string :as gstring]
            [manage.adset-decision-tree-page.event :as event]
            [manage.adset-decision-tree-page.sub :as sub]
            [manage.adset-decision-tree-page.view.node-conversion
             :as node-conversion]
            [re-frame.core :as rf]
            [uix.core :as uix :refer [$ defhook defui]]
            [uix.re-frame :refer [use-subscribe]]))

(def edge-margin 4)
(def straight-line-length 12)

;; The X pixel distance between the root node's handle's outer margin and the
;; starting point of the edge. The edge starts "within" the handle's border,
;; which is only visible when the user hovers the handle.
(def root-handle-start-adjustment 4)

(defhook use-edge-source-x
  [{:keys [source sourceX]}]
  (let [{{{:keys [x]} :positionAbsolute} :internals
         {:keys [width]} :measured} (->clj (useInternalNode source))
        {node-type :type} (use-subscribe [::sub/node-data (parse-long source)])
        source-is-root? (node-conversion/root-node-type? node-type)]
    (if source-is-root?
      (- sourceX root-handle-start-adjustment)
      (+ x width edge-margin))))

(defhook use-edge-target-x
  [{:keys [target]}]
  (let [{{{:keys [x]} :positionAbsolute} :internals} (->clj (useInternalNode target))]
    (- x edge-margin)))

(defui line-actions [{:keys [x y edge-id]}]
  (let [read-only (use-subscribe [::sub/editor-read-only])]
    ($ Paper
       {:elevation 2
        :sx #js {:zIndex 1
                 :position "absolute"
                 :transform (gstring/format
                             "translate(-50%, -150%) translate(%spx,%spx)"
                             x y)
                 :pointerEvents "all"
                 :borderRadius 1
                 :p 0.5}}
       ($ Stack
          {:direction "row"
           :gap 0.5
           :class "nopan nodrag"}
          ($ IconButton
             {:onClick (fn [e]
                         (.stopPropagation e)
                         (rf/dispatch [::event/update-sub-path-visibility
                                       {:edge-id edge-id
                                        :is-visible false}]))
              :sx #js {:p 0.5}}
             ($ Icon "remove_circle_outline"))
          (when-not read-only
            ($ :<>
               ($ Divider
                  {:orientation "vertical"
                   :flexItem true})
               ($ IconButton
                  {:onClick (fn [e]
                              (.stopPropagation e)
                              ;; TODO: create dedicated event for removing an edge
                              (rf/dispatch [::event/on-edges-change
                                            #js [#js {:id edge-id :type "remove"}]]))
                   :sx #js {:p 0.5}}
                  ($ Icon "delete_outline"))))))))

(defn edge [^js props]
  (let [{:keys [sourceY targetY targetPosition sourcePosition style selected data id]
         :as props} (->clj props)

        ;; draw the edge with `edge-margin` margin from the decision node
        ;; regardless of the outcome's handle's position and of the target
        ;; node's handle's dimensions
        source-x (use-edge-source-x props)
        target-x (use-edge-target-x props)
        straight-source-x (- target-x straight-line-length)

        [main-path] (getBezierPath
                     #js {:sourceX source-x
                          :sourceY sourceY
                          :targetX straight-source-x
                          :targetY targetY
                          :targetPosition targetPosition
                          :sourcePosition sourcePosition
                          :curvature 1})

        ;; draw a straight horizontal line inside the arrow's head
        ;; to avoid the bezier line crossing it in steep angles
        [path-ending] (getStraightPath
                       #js {:sourceX straight-source-x
                            :sourceY targetY
                            :targetX target-x
                            :targetY targetY})

        label-position (:label-position data)

        dragging-node? (use-element-presence ".react-flow__node.draggable.dragging")]
    ($ :<>
       ($ BaseEdge (assoc props
                          :path (str main-path " " path-ending)
                          :style (merge style
                                        (when-not dragging-node?
                                          {:transition "0.2s"}))))
       ($ EdgeLabelRenderer
          (when selected
            ($ line-actions
               {:edge-id id
                :x (:x label-position)
                :y (:y label-position)}))))))

(def default-edge-options
  {:markerEnd {:type (.-Arrow MarkerType)
               :width 20
               :height 20}
   :style {:strokeWidth 2}})

(defn set-edge-color [edge-opts color]
  (-> edge-opts
      (assoc-in [:markerEnd :color] color)
      (assoc-in [:style :stroke] color)))

(defn set-edge-dashed [edge-opts]
  (assoc-in edge-opts [:style :strokeDasharray] 4))

(defhook use-update-edge-color-by-selection []
  (let [main-color (.. (styles/useTheme) -palette -primary -main)
        highlighted-edge-ids (use-subscribe [::sub/highlighted-edge-ids])
        dashed-edge-id (use-subscribe [::sub/dashed-edge-id])
        edge-updater
        (uix/use-callback (fn [^js obj]
                            (let [{:keys [selected id] :as clj-obj} (->clj obj)
                                  highlighted (contains? highlighted-edge-ids id)
                                  dashed (= dashed-edge-id id)]
                              (cond-> clj-obj
                                (or selected highlighted dashed) (-> (merge default-edge-options)
                                                                     (set-edge-color main-color))
                                dashed (set-edge-dashed)
                                true ->js)))
                          [main-color highlighted-edge-ids dashed-edge-id])]
    edge-updater))

(defhook use-default-edge-props []
  (let [reactflow-instance (useReactFlow)
        default-edge-color (-> (styles/useTheme) .-palette .-grey (gobject/get 40))
        on-edge-click (uix/use-callback
                       (fn [e ^js edge]
                         (let [position (.screenToFlowPosition reactflow-instance
                                                               #js {:x (.-clientX e)
                                                                    :y (.-clientY e)})]
                           (rf/dispatch [::event/assoc-reactflow-edge
                                         (.-id edge) :data {:label-position position}])))
                       [reactflow-instance])
        connection-line-style (uix/use-memo (fn [] #js {:strokeWidth 2
                                                        :stroke default-edge-color})
                                            [default-edge-color])
        default-edge-options (uix/use-memo
                              #(->js (set-edge-color default-edge-options default-edge-color))
                              [default-edge-color])]
    {:onEdgeClick on-edge-click
     :connectionLineStyle connection-line-style
     :defaultEdgeOptions default-edge-options}))
